import type { NextComponentType } from 'next';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { Banner, BANNER_TYPES } from '@sravni/react-advertisement';
import { PreFooter } from '@sravni/react-pre-footer';
import { Interceptor } from '@sravni/ugc/lib/utils/sentry/Interceptor';

import { RatingsContent } from '@components/RatingsContent';

import { DEFAULT_RATING_REGION } from '../../constants/locations';
import { useIsWlPage } from '../../hooks/useIsWlPage';
import { useMetadataByPath } from '../../hooks/useMetadataByPath';
import { prepareSeoParams } from '../../server/helpers/prepareSeoParams';
import { useBanners } from '../../store/hooks/advertising';
import { useFilters } from '../../store/hooks/filters';
import { useCurrentUserLocation } from '../../store/hooks/locations';
import { setFilters } from '../../store/reducers/filters';
import { fetchMetadata } from '../../store/reducers/metadata';
import { setDictionaries, setProductConfig, setProducts } from '../../store/reducers/productPopupInfo';
import { setRating } from '../../store/reducers/ratings';
import type { TQuery } from '../../types/router';
import { isServer } from '../../utils/isServer';
import { AB_NAME } from '../_app';

const ListPage: NextComponentType<App.ReduxNextPageContext<TQuery>> = () => {
  const banners = useBanners();
  const dispatch = useDispatch();
  const metadata = useMetadataByPath();
  const userLocation = useCurrentUserLocation();
  const filters = useFilters();
  const isWl = useIsWlPage();

  useEffect(() => {
    dispatch(
      fetchMetadata(
        prepareSeoParams({
          filters: {
            ...filters,
            location: filters.location ?? DEFAULT_RATING_REGION,
          },
        }),
      ),
    );
  }, [filters, userLocation, dispatch]);

  return (
    <Interceptor componentName="rating-list-component">
      <RatingsContent />
      {!!metadata?.prefooter && (
        <div className="h-mt-40">
          <PreFooter columns={metadata?.prefooter} />
        </div>
      )}
      {!isWl && (
        <Banner banner={banners?.[BANNER_TYPES.OUT_OF_PAGE]} type={BANNER_TYPES.OUT_OF_PAGE} abName={AB_NAME} />
      )}
    </Interceptor>
  );
};

ListPage.getInitialProps = (context: App.ReduxNextPageContext<TQuery>) => {
  const { store } = context;

  const { dispatch } = store;
  if (isServer) {
    if (context.req.__FILTERS__) {
      dispatch(setFilters(context.req.__FILTERS__));
    }

    if (context.req.__RATING__) {
      dispatch(setRating(context.req.__RATING__));
    }
    if (context.req.__PRODUCT_CONFIG__) {
      dispatch(setProductConfig(context.req.__PRODUCT_CONFIG__));
    }
    if (context.req.__DICTIONARIES__) {
      dispatch(setDictionaries(context.req.__DICTIONARIES__));
    }
    if (context.req.__PRODUCTS_BY_ID__) {
      dispatch(setProducts(context.req.__PRODUCTS_BY_ID__));
    }
  }

  return {};
};

export default ListPage;
